import { useState, Fragment } from 'react';
import { useOperators, useSystemPm2, useSystemLogs, useCrDrainHealth } from '../lib/api';
import StatusBadge from '../components/StatusBadge.jsx';
import { formatDistanceToNow } from 'date-fns';

function fmtTime(iso) {
  if (!iso) return '—';
  try {
    // S79: server stores some timestamps in SQLite space format ('YYYY-MM-DD HH:MM:SS')
    // which `new Date()` parses as LOCAL time in Chrome — not UTC. The DB stores UTC,
    // so the parsed Date was shifted by the user's timezone offset, producing nonsense
    // like "in about 7 hours" for a batch that was actually 1 minute ago. Normalize
    // space-format to ISO-Z explicitly. Already-ISO strings (with T or Z) pass through.
    const norm = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(iso)
      ? iso.replace(' ', 'T') + 'Z'
      : iso;
    return formatDistanceToNow(new Date(norm), { addSuffix: true });
  } catch (_) { return iso; }
}

function StatusPill({ status }) {
  const map = {
    running:  { tone: 'green', label: '🟢 Running' },
    stalled:  { tone: 'amber', label: '🟡 Stalled' },
    sleeping: { tone: 'gray',  label: '😴 Sleeping (off-hours)' },
    down:     { tone: 'red',   label: '🔴 Down' },
    'no-data':{ tone: 'gray',  label: 'No data' },
    unknown:  { tone: 'gray',  label: 'Unknown' },
  };
  const x = map[status] || map.unknown;
  return <StatusBadge badge={{ tone: x.tone, label: x.label }} />;
}

function OperatorTile({ op }) {
  const today = op.today || {};
  const last = op.last_batch;
  return (
    <div className="card p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-lg capitalize">{op.operator_id}</h3>
        <StatusPill status={op.status} />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3 text-sm">
        <div>
          <div className="text-slate-500 text-xs">Pending</div>
          <div className="font-semibold">{op.queue.pending ?? 0}</div>
        </div>
        <div>
          <div className="text-slate-500 text-xs">Held</div>
          <div className="font-semibold">{op.queue.held ?? 0}</div>
        </div>
        <div>
          <div className="text-slate-500 text-xs">Do Not Post</div>
          <div className="font-semibold">{op.queue.do_not_post ?? 0}</div>
        </div>
        <div>
          <div className="text-slate-500 text-xs">Completed</div>
          <div className="font-semibold">{op.queue.completed ?? 0}</div>
        </div>
      </div>

      <div className="text-xs text-slate-500 border-t pt-2 space-y-1">
        <div>
          <strong>Last batch:</strong>{' '}
          {last ? `${fmtTime(last.batch_started_at)} · ${last.total_seconds}s` : '—'}
        </div>
        <div>
          <strong>Trips today (24h):</strong>{' '}
          {today.trips?.trips
            ? `${today.trips.trips} unique · ✓${today.trips.posted} posted · ✗${today.trips.declined} declined · ⊘${today.trips.do_not_post} do_not_post`
            : '0'}
        </div>
        <div>
          <strong>Quotes today:</strong>{' '}
          {today.quotes?.quotes
            ? `${today.quotes.quotes} total · ${today.quotes.sent_to_customer} sent · ${today.quotes.posted_to_avinode} on Avinode · ${today.quotes.held} held`
            : '0'}
        </div>
        <div>
          <strong>Batches:</strong>{' '}
          {today.batches
            ? `${today.batches} · avg ${Math.round(today.avg_total_seconds ?? 0)}s · max ${today.max_total_seconds ?? 0}s`
            : '—'}
        </div>
      </div>
    </div>
  );
}

function fmtUptime(seconds) {
  if (seconds == null) return '—';
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h${Math.floor((seconds % 3600) / 60)}m`;
  return `${Math.floor(seconds / 86400)}d${Math.floor((seconds % 86400) / 3600)}h`;
}

function ProcessRow({ p, onSelect }) {
  const isOnline = p.status === 'online';
  const dot = isOnline ? '🟢' : (p.status === 'waiting restart' ? '🟡' : '🔴');
  // Continuous-loop processes (sqp-auto-*) restart on every batch cycle by design,
  // so the count is a "cycles completed" metric, not crash signal. Render with a
  // 'cycles' suffix + tooltip so a high number doesn't look like a problem.
  const countLabel = p.is_continuous_loop
    ? <span title="Continuous-loop scheduler: each batch is an intentional exit+restart" className="text-slate-500">{p.restart_count} <span className="text-[10px] uppercase">cycles</span></span>
    : p.restart_count;
  return (
    <tr
      className="border-t text-xs cursor-pointer hover:bg-slate-50"
      onClick={() => onSelect(p.name)}
      title="Click to view recent logs"
    >
      <td className="py-1 pr-2 font-mono text-tint">{dot} {p.name}</td>
      <td className="py-1 pr-2">{p.status}</td>
      <td className="py-1 pr-2 text-right">{fmtUptime(p.uptime_seconds)}</td>
      <td className="py-1 pr-2 text-right">{countLabel}</td>
      <td className="py-1 pr-2 text-right">{p.cpu_percent != null ? `${p.cpu_percent}%` : '—'}</td>
      <td className="py-1 text-right">{p.memory_mb != null ? `${p.memory_mb}MB` : '—'}</td>
    </tr>
  );
}

function LogModal({ processName, onClose }) {
  const [lines, setLines] = useState(50);
  const [stream, setStream] = useState('both');
  const { data, isLoading, isError, error } = useSystemLogs(processName, { lines, stream });

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center px-4" onClick={onClose}>
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <div className="p-4 border-b flex items-center justify-between">
          <div>
            <h2 className="font-semibold text-lg">📋 Logs — <span className="font-mono text-tint">{processName}</span></h2>
            <div className="text-xs text-slate-500">Auto-refreshes every 10s</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 text-2xl leading-none">×</button>
        </div>

        <div className="p-3 border-b flex flex-wrap items-center gap-3 text-sm">
          <label className="flex items-center gap-2">
            <span className="text-slate-600">Lines:</span>
            <select value={lines} onChange={e => setLines(Number(e.target.value))} className="border rounded px-2 py-1">
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={250}>250</option>
              <option value={500}>500</option>
            </select>
          </label>
          <label className="flex items-center gap-2">
            <span className="text-slate-600">Stream:</span>
            <select value={stream} onChange={e => setStream(e.target.value)} className="border rounded px-2 py-1">
              <option value="both">Both</option>
              <option value="out">stdout only</option>
              <option value="err">stderr only</option>
            </select>
          </label>
        </div>

        <div className="flex-1 overflow-y-auto p-3 bg-slate-900 text-slate-200 font-mono text-xs">
          {isLoading && <div className="text-slate-400">Loading…</div>}
          {isError && <div className="text-rose-400">Error: {error.message}</div>}
          {data && (
            <>
              {data.err?.exists && data.err.content && (
                <div className="mb-4">
                  <div className="text-rose-400 font-semibold mb-1">— stderr —</div>
                  <pre className="whitespace-pre-wrap">{data.err.content}</pre>
                </div>
              )}
              {data.out?.exists && data.out.content && (
                <div>
                  <div className="text-emerald-400 font-semibold mb-1">— stdout —</div>
                  <pre className="whitespace-pre-wrap">{data.out.content}</pre>
                </div>
              )}
              {!data.out?.content && !data.err?.content && (
                <div className="text-slate-400">No log output.</div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function SystemHealthPanel() {
  const { data, isLoading, isError, error } = useSystemPm2(15);
  const [activeLogs, setActiveLogs] = useState(null);

  if (isLoading) return (
    <div className="card p-4">
      <h2 className="font-semibold text-lg mb-2">System Health (PM2)</h2>
      <div className="text-sm text-slate-500">Loading…</div>
    </div>
  );
  if (isError) return (
    <div className="card p-4">
      <h2 className="font-semibold text-lg mb-2">System Health (PM2)</h2>
      <div className="text-sm text-rose-700">Error: {error.message}</div>
    </div>
  );

  const overallTone = data.overall === 'green' ? 'green' : data.overall === 'amber' ? 'amber' : 'red';
  const overallSymbol = data.overall === 'green' ? '✓' : data.overall === 'amber' ? '⚠' : '✗';
  const overallLabel = data.overall === 'green' ? 'All systems nominal'
    : data.overall === 'amber' ? `Unstable: ${data.unstable.join(', ')}`
    : data.missing.length > 0 ? `Missing: ${data.missing.join(', ')}`
    : `Stopped: ${data.stopped.join(', ')}`;

  return (
    <>
      <div className="card p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold text-lg">System Health (PM2)</h2>
          <StatusBadge badge={{ tone: overallTone, symbol: overallSymbol, label: overallLabel }} />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-slate-500">
                <th className="py-1 pr-2">Process</th>
                <th className="py-1 pr-2">Status</th>
                <th className="py-1 pr-2 text-right">Uptime</th>
                <th className="py-1 pr-2 text-right">Restarts</th>
                <th className="py-1 pr-2 text-right">CPU</th>
                <th className="py-1 text-right">Memory</th>
              </tr>
            </thead>
            <tbody>
              {data.processes.map(p => <ProcessRow key={p.name} p={p} onSelect={setActiveLogs} />)}
            </tbody>
          </table>
        </div>
        <div className="text-xs text-slate-400 mt-2 flex items-center justify-between">
          <span>{data.cached ? `Cached ${formatDistanceToNow(new Date(data.cached_at), { addSuffix: true })}` : 'Live'}</span>
          <span className="hidden sm:inline">Click a process row to view recent logs</span>
        </div>
      </div>
      {activeLogs && <LogModal processName={activeLogs} onClose={() => setActiveLogs(null)} />}
    </>
  );
}

// The cr-run DRAIN loops (CR intake pollers) are NOT PM2 processes, so PM2 health can't see them —
// this reads cr-drain-health.sh's status JSON. Surfaces the exact signals that would have caught the
// 2026-07-13 incident: per-loop up/down, quote thrash %, the TZ guard, host-intake liveness, and the
// disabled-search screenshot rate.
export function CrDrainHealthPanel() {
  const { data, isLoading, isError, error } = useCrDrainHealth(30);

  const Shell = ({ badge, children }) => (
    <div className="card p-4">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-semibold text-lg">CR Drain Health <span className="text-xs font-normal text-slate-400">(intake pollers — not PM2)</span></h2>
        {badge}
      </div>
      {children}
    </div>
  );

  if (isLoading) return <Shell><div className="text-sm text-slate-500">Loading…</div></Shell>;
  if (isError)   return <Shell><div className="text-sm text-rose-700">Error: {error.message}</div></Shell>;
  if (!data.available) return (
    <Shell badge={<StatusBadge badge={{ tone: 'gray', label: 'No data yet' }} />}>
      <div className="text-xs text-slate-500">{data.reason || 'monitor has not written a status yet'}</div>
    </Shell>
  );

  const s = data.status || {};
  const tone = data.stale ? 'gray' : (s.healthy ? 'green' : 'red');
  const symbol = data.stale ? '?' : (s.healthy ? '✓' : '✗');
  const label = data.stale ? 'Stale — monitor not reporting' : (s.healthy ? 'Healthy' : 'UNHEALTHY');

  return (
    <Shell badge={<StatusBadge badge={{ tone, symbol, label }} />}>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-slate-500">
              <th className="py-1 pr-2">Operator loop</th>
              <th className="py-1 pr-2">Loop</th>
              <th className="py-1 pr-2 text-right">Attempts</th>
              <th className="py-1 pr-2 text-right">Held-for-retry</th>
              <th className="py-1 text-right">Thrash</th>
            </tr>
          </thead>
          <tbody>
            {(s.ops || []).map(o => {
              const bad = o.thrash_pct >= 60 || !o.loop_up;
              const qh = o.quoting_hours;
              return (
                <Fragment key={o.op}>
                <tr className="border-t text-xs">
                  <td className="py-1 pr-2 font-mono text-tint">{o.loop_up ? '🟢' : '🔴'} loop-{o.op}</td>
                  <td className="py-1 pr-2">{o.loop_up ? 'up' : 'DOWN'}</td>
                  <td className="py-1 pr-2 text-right">{o.attempts}</td>
                  <td className="py-1 pr-2 text-right">{o.holds}</td>
                  <td className={`py-1 text-right ${bad ? 'text-rose-600 font-semibold' : ''}`}>{o.thrash_pct}%</td>
                </tr>
                {qh && (qh.unknown || qh.valid === false || (qh.configured && qh.within_now === false)) && (
                  <tr className="text-xs" data-testid={`drain-hours-${o.op}`}>
                    <td colSpan={5} className="pb-1 pr-2 text-label-secondary">
                      {qh.unknown
                        ? 'Quoting hours could not be read'
                        : qh.valid === false
                          ? <span className="text-critical">Quoting hours are invalid, so quoting is blocked until they are fixed</span>
                          : <>Outside quoting hours &mdash; opens {qh.opens_label}. A quiet drain is expected.</>}
                    </td>
                  </tr>
                )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="text-xs text-slate-500 border-t mt-2 pt-2 flex flex-wrap gap-x-4 gap-y-1 items-center">
        <span>TZ guard: {s.tz_ok ? <span className="text-emerald-600">✓ {s.tz_expect}</span> : <span className="text-rose-600 font-semibold">✗ WRONG — same-day CRs will false-flag past</span>}</span>
        <span>Host intake: {s.host_up ? '🟢 up' : '🔴 down'}</span>
        <span>Disabled-Search shots (15m): <span className={s.screenshots_15m >= 40 ? 'text-rose-600 font-semibold' : ''}>{s.screenshots_15m}</span></span>
        <span className="ml-auto text-slate-400">{s.ts ? fmtTime(s.ts) : ''}</span>
      </div>
    </Shell>
  );
}

export default function LiveOps() {
  const { data, isLoading, isError, error } = useOperators(30);

  if (isLoading) return <div className="text-slate-500">Loading…</div>;
  if (isError)   return <div className="text-rose-700">Error: {error.message}</div>;

  const op = data.operating_hours;
  return (
    <div className="space-y-6">
      <div className="card p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <h2 className="font-semibold text-lg">Operating Hours</h2>
          <div className="text-sm text-slate-600">{op.window}</div>
        </div>
        <div className="text-sm">
          <div>{op.local_display}</div>
          {op.in_window ? (
            <StatusBadge badge={{ tone: 'green', symbol: '✓', label: 'In window — schedulers active' }} />
          ) : (
            <StatusBadge badge={{ tone: 'gray', symbol: '😴', label: `Off-hours — resumes in ${Math.floor(op.minutes_until_next_window/60)}h ${op.minutes_until_next_window%60}m` }} />
          )}
        </div>
      </div>

      <SystemHealthPanel />

      <CrDrainHealthPanel />

      <div>
        <h2 className="font-semibold text-lg mb-3">Operators</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {data.operators.map(op => <OperatorTile key={op.operator_id} op={op} />)}
        </div>
      </div>
    </div>
  );
}
